/**
 * New App: one dialog, two steps. Step 1 names the app. Step 2 adds its
 * first variant, the copy of the app at a web address that tests run
 * against. Finishing creates both, so a new app is never left with
 * nothing to test.
 *
 * It replaced a New App dialog that saved only the name and closed. The
 * step that made the app usable (Add variant, on the app's Variants tab)
 * was a second dialog nothing on screen pointed to.
 *
 * Saving rules, which the tests pin:
 * - Nothing is saved until a finish button on step 2 is pressed, so
 *   closing earlier creates nothing. Saving the app on step 1's Next was
 *   rejected: every cancel on step 2 would leave an empty app behind.
 * - Finishing makes the same two calls the Apps page makes elsewhere:
 *   the app, then the variant. If the variant fails, the dialog stays on
 *   step 2 and a retry creates only the variant, under the app already
 *   saved. Back is gone from then on, since editing a saved app would be
 *   a third save with its own failure case; the app's edit button still
 *   renames it later.
 * - While the saves run, nothing can be changed or closed.
 *
 * Reloading the app list, selecting the new app and starting the scan
 * stay with the page (`onCreated`), which owns that state.
 *
 * Lives in `components/` because it is a dialog with its own state, like
 * the others here; the Apps page was already the largest file in
 * `pages/`. The variant fields are the shared `VariantFormFields`, the
 * same form the New Variant dialog uses.
 */
import { useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'
import { ArrowLeft, ArrowRight, Check, Loader2, Search } from 'lucide-react'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@/components/ui'
import { applicationsAPI, variantsAPI } from '@/lib/api'
import {
  VariantFormFields,
  computeVariantFormState,
  isVariantFormComplete,
  variantFieldsFrom,
  type VariantFormState,
} from '@/components/VariantFormFields'

export const VARIANT_NOT_SAVED_MESSAGE = "The app was saved, but the variant wasn't. Check the details and try again."
export const APP_NOT_SAVED_MESSAGE = "The app couldn't be saved. Check your connection and try again."

export interface NewAppResult {
  appId: string
  /** Null when the user closed the dialog after the app saved and the
   * variant did not. */
  variantId: string | null
  /** True when the user pressed Create & Scan. */
  scan: boolean
}

export function NewAppWizard({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called after the dialog closes on a saved app: with its variant
   * when both saved, or without one when the user closed it after the
   * variant failed. */
  onCreated: (result: NewAppResult) => void
}) {
  const [step, setStep] = useState<1 | 2>(1)
  const [appName, setAppName] = useState('')
  const [appDescription, setAppDescription] = useState('')
  const [variantForm, setVariantForm] = useState<VariantFormState>(() => computeVariantFormState(null))
  // Set once the app is saved, so a retry after a failed variant adds the
  // variant to this app instead of creating a second one.
  const [savedAppId, setSavedAppId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // State updates land after the click that caused them, so a fast
  // double click could start two saves. The ref closes that gap.
  const savingRef = useRef(false)

  function reset() {
    setStep(1)
    setAppName('')
    setAppDescription('')
    setVariantForm(computeVariantFormState(null))
    setSavedAppId(null)
    setError(null)
  }

  /** Every close goes through here: the X, Escape, a click outside and
   * Cancel. Ignored while saving. Closing after the app saved still
   * hands it to the page, so it appears with its empty Variants tab. */
  function handleOpenChange(next: boolean) {
    if (next) {
      onOpenChange(true)
      return
    }
    if (savingRef.current) return
    const keptAppId = savedAppId
    reset()
    onOpenChange(false)
    if (keptAppId) onCreated({ appId: keptAppId, variantId: null, scan: false })
  }

  async function finish(scan: boolean) {
    if (savingRef.current || !isVariantFormComplete(variantForm)) return
    savingRef.current = true
    setSaving(true)
    setError(null)
    try {
      let appId = savedAppId
      if (!appId) {
        try {
          const created = await applicationsAPI.create({
            name: appName,
            description: appDescription || undefined,
            templates: [],
          })
          appId = created.id
          setSavedAppId(appId)
        } catch (err) {
          console.error('Failed to save application:', err)
          setError(APP_NOT_SAVED_MESSAGE)
          return
        }
      }
      const variantId = uuid()
      try {
        await variantsAPI.create({
          _id: variantId,
          applicationId: appId,
          ...variantFieldsFrom(variantForm),
        } as any)
      } catch (err) {
        console.error('Failed to save variant:', err)
        setError(VARIANT_NOT_SAVED_MESSAGE)
        return
      }
      reset()
      onOpenChange(false)
      onCreated({ appId, variantId, scan })
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }

  const canFinish = isVariantFormComplete(variantForm) && !saving

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} closeOnEscape={!saving}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" closeDisabled={saving}>
        <DialogHeader>
          <DialogTitle>New Application</DialogTitle>
          <ol className="flex items-center gap-2 pt-2 text-xs text-muted-foreground" aria-label={`Step ${step} of 2`}>
            <li className={`flex items-center gap-1.5 min-w-0 ${step === 1 ? 'font-medium text-foreground' : ''}`} aria-current={step === 1 ? 'step' : undefined}>
              <span
                className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold ${
                  step === 1 ? 'border-primary bg-primary text-primary-foreground' : 'border-primary/30 bg-primary/10 text-primary'
                }`}
              >
                {step === 1 ? '1' : <Check className="h-3 w-3" aria-hidden="true" />}
              </span>
              <span className="truncate">
                {step === 1 ? 'Your app' : savedAppId ? `${appName} saved` : appName}
              </span>
            </li>
            <li className="h-px flex-1 min-w-4 bg-border" aria-hidden="true" />
            <li className={`flex items-center gap-1.5 shrink-0 ${step === 2 ? 'font-medium text-foreground' : ''}`} aria-current={step === 2 ? 'step' : undefined}>
              <span
                className={`inline-flex h-5 w-5 items-center justify-center rounded-full border text-[11px] font-semibold ${
                  step === 2 ? 'border-primary bg-primary text-primary-foreground' : 'bg-background'
                }`}
              >
                2
              </span>
              Where it runs
            </li>
          </ol>
        </DialogHeader>

        {step === 1 ? (
          <>
            <DialogBody className="space-y-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={appName} onChange={(e) => setAppName(e.target.value)} placeholder="My App" />
              </div>
              <div className="space-y-2">
                <Label>Description (optional)</Label>
                <Textarea
                  value={appDescription}
                  onChange={(e) => setAppDescription(e.target.value)}
                  placeholder="Brief description of the application"
                  rows={2}
                />
              </div>
            </DialogBody>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={() => setStep(2)} disabled={!appName}>
                Next
                <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogBody className="space-y-4">
              <p className="text-sm text-muted-foreground">
                A variant is one copy of your app at its own web address, like production or staging. Add the first one now. You can add more later.
              </p>
              <VariantFormFields value={variantForm} onChange={setVariantForm} disabled={saving} />
              {error && (
                <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </p>
              )}
            </DialogBody>
            <DialogFooter className="flex items-center justify-between">
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground underline disabled:no-underline disabled:opacity-50"
                disabled={!canFinish}
                onClick={() => finish(false)}
              >
                Create without scanning
              </button>
              <div className="flex gap-2">
                {!savedAppId && (
                  <Button variant="outline" onClick={() => setStep(1)} disabled={saving}>
                    <ArrowLeft className="h-3 w-3 mr-1" />
                    Back
                  </Button>
                )}
                <Button onClick={() => finish(true)} disabled={!canFinish}>
                  {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Search className="h-3 w-3 mr-1" />}
                  Create &amp; Scan
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
